/**
 * Dynamic Segmentation Panel
 * Allows users to create and view dynamic persona segmentations
 */

import React, { useState, useEffect, useCallback, useImperativeHandle, forwardRef } from 'react';
import { Sparkles, Loader2, AlertCircle, ChevronDown, ChevronUp, Trash2 } from 'lucide-react';
import useSegmentationSocket from '../../hooks/useSegmentationSocket';
import { segmentationService, Segmentation, SuggestedAxis } from '../../services/segmentationService';
import DynamicSegmentResults from './DynamicSegmentResults';
import Button from '../Button';

export interface DynamicSegmentationPanelRef {
  createSegmentation: () => Promise<void>;
  isReady: () => boolean;
  isProcessing: () => boolean;
  hasSegmentation: () => boolean;
}

interface DynamicSegmentationPanelProps {
  audienceId: number;
  onSegmentationComplete?: (segmentation: Segmentation) => void;
  onSegmentsDiscovered?: (segments: any[], assignmentProgress?: { completed: number; total: number }) => void;
  onSelectSegments?: (segmentIndices: number[], personaIds: number[]) => void;
  onProcessingChange?: (isProcessing: boolean) => void;
  selectedSegmentIndices?: number[];
  className?: string;
}

const DynamicSegmentationPanel = forwardRef<DynamicSegmentationPanelRef, DynamicSegmentationPanelProps>(({
  audienceId,
  onSegmentationComplete,
  onSegmentsDiscovered,
  onSelectSegments,
  onProcessingChange,
  selectedSegmentIndices = [],
  className = ''
}, ref) => {
  // State
  const [axis, setAxis] = useState('');
  const [existingSegmentations, setExistingSegmentations] = useState<Segmentation[]>([]);
  const [suggestedAxes, setSuggestedAxes] = useState<SuggestedAxis[]>([]);
  const [selectedSegmentation, setSelectedSegmentation] = useState<Segmentation | null>(null);
  const [isLoadingExisting, setIsLoadingExisting] = useState(true);
  const [showExisting, setShowExisting] = useState(true);

  // Track when fetching final results after processing completes
  const [isFetchingResults, setIsFetchingResults] = useState(false);

  // WebSocket hook
  const {
    segmentationState,
    startSegmentation,
    fetchSegmentation,
    cancelSegmentation
  } = useSegmentationSocket({
    onProcessingCompleted: async (data) => {
      // Fetch the full segmentation data
      setIsFetchingResults(true);
      try {
        const segmentation = await fetchSegmentation(data.segmentation_id);
        setSelectedSegmentation(segmentation);
        // Refresh the list
        loadExistingSegmentations();
        onSegmentationComplete?.(segmentation);
      } catch (error) {
        console.error('Failed to fetch completed segmentation:', error);
      } finally {
        setIsFetchingResults(false);
      }
    },
    autoConnect: true
  });

  // Load existing segmentations for this audience
  const loadExistingSegmentations = useCallback(async () => {
    try {
      setIsLoadingExisting(true);
      const [segmentations, axes] = await Promise.all([
        segmentationService.listForAudience(audienceId),
        segmentationService.getSuggestedAxes(audienceId)
      ]);
      setExistingSegmentations(segmentations.filter(s => s.status === 'completed'));
      setSuggestedAxes(axes);
    } catch (error) {
      console.error('Failed to load existing segmentations:', error);
    } finally {
      setIsLoadingExisting(false);
    }
  }, [audienceId]);

  useEffect(() => {
    loadExistingSegmentations();
  }, [loadExistingSegmentations]);

  // Notify parent of processing state changes (include fetching results)
  useEffect(() => {
    onProcessingChange?.(segmentationState.isProcessing || isFetchingResults);
  }, [segmentationState.isProcessing, isFetchingResults, onProcessingChange]);

  // Notify parent when segment definitions are discovered (for early UI update)
  useEffect(() => {
    if (segmentationState.pendingSegments && segmentationState.pendingSegments.length > 0) {
      onSegmentsDiscovered?.(
        segmentationState.pendingSegments,
        segmentationState.assignmentProgress || undefined
      );
    }
  }, [segmentationState.pendingSegments, segmentationState.assignmentProgress, onSegmentsDiscovered]);

  // Handle create segmentation
  const handleCreateSegmentation = async () => {
    if (!axis.trim()) return;

    try {
      const response = await startSegmentation({
        audience_id: audienceId,
        segmentation_axis: axis.trim(),
        use_v2: true  // Use v2 pipeline with attribute extraction
      });

      // If cached/sync response, select it immediately
      if (response.cached || response.segments) {
        const segmentation = await fetchSegmentation(response.segmentation_id);
        setSelectedSegmentation(segmentation);
        loadExistingSegmentations();
        onSegmentationComplete?.(segmentation);
      }
    } catch (error) {
      console.error('Failed to create segmentation:', error);
    }
  };

  // Expose methods to parent via ref
  useImperativeHandle(ref, () => ({
    createSegmentation: handleCreateSegmentation,
    isReady: () => !!axis.trim(),
    isProcessing: () => segmentationState.isProcessing,
    hasSegmentation: () => !!(selectedSegmentation && selectedSegmentation.status === 'completed')
  }), [axis, segmentationState.isProcessing, selectedSegmentation, handleCreateSegmentation]);

  // Handle select existing segmentation
  const handleSelectExisting = async (seg: Segmentation) => {
    try {
      const fullSegmentation = await fetchSegmentation(seg.id);
      setSelectedSegmentation(fullSegmentation);
      // Also notify parent that a segmentation was selected
      onSegmentationComplete?.(fullSegmentation);
      // Clear any previous segment selections when switching segmentations
      onSelectSegments?.([], []);
    } catch (error) {
      console.error('Failed to fetch segmentation:', error);
    }
  };

  // Handle delete segmentation
  const handleDeleteSegmentation = async (id: number, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!confirm('Are you sure you want to delete this segmentation?')) return;

    try {
      await segmentationService.delete(id);
      if (selectedSegmentation?.id === id) {
        setSelectedSegmentation(null);
      }
      loadExistingSegmentations();
    } catch (error) {
      console.error('Failed to delete segmentation:', error);
    }
  };

  // Handle segment selection
  const handleSegmentSelection = (segmentIndex: number, _personaIds: number[]) => {
    if (!onSelectSegments) return;

    const newIndices = selectedSegmentIndices.includes(segmentIndex)
      ? selectedSegmentIndices.filter(i => i !== segmentIndex)
      : [...selectedSegmentIndices, segmentIndex];

    // Collect all persona IDs from selected segments
    const allPersonaIds: number[] = [];
    if (selectedSegmentation) {
      newIndices.forEach(idx => {
        const segment = selectedSegmentation.segment_definitions[idx];
        if (segment) {
          allPersonaIds.push(...segment.persona_ids);
        }
      });
    }

    onSelectSegments(newIndices, allPersonaIds);
  };

  // Handle suggested axis click
  const handleSuggestedAxisClick = (suggestedAxis: SuggestedAxis) => {
    setAxis(suggestedAxis.axis);
    // Find and select the existing segmentation
    const existing = existingSegmentations.find(s => s.id === suggestedAxis.id);
    if (existing) {
      handleSelectExisting(existing);
    }
  };

  return (
    <div className={`border border-gray-200 rounded-xl p-6 sm:p-8 bg-white ${className}`}>
      {/* Create New */}
      <div className="space-y-4">
        <div>
          {/* Big question — establishes hierarchy, concept-test style */}
          <h3 className="flex items-start gap-2.5 text-xl sm:text-2xl font-semibold text-[#1C1C1E] tracking-tight leading-snug">
            <Sparkles className="w-5 h-5 sm:w-6 sm:h-6 text-[#07E5D1] flex-shrink-0 mt-1" />
            <span>How would you like to segment your audience?</span>
          </h3>
          <p className="text-sm sm:text-[15px] text-[#6B7280] mt-2 leading-relaxed">
            Describe the groups you want to see in your own words — full sentences are perfect.
          </p>

          <textarea
            value={axis}
            onChange={(e) => setAxis(e.target.value)}
            placeholder={"For example: I'd like to split my audience into health-conscious shoppers who read every label, busy people who just want something quick and convenient, and budget-minded buyers who always hunt for the best deal.\n\nOr: Group them by where they are in life — young singles, new parents, empty nesters, and retirees."}
            className="w-full mt-5 px-4 py-3 border border-gray-200 rounded-lg
                       focus:ring-2 focus:ring-[#07E5D1]/20 focus:border-[#07E5D1]
                       text-[#1C1C1E] text-base placeholder-[#9CA3AF] transition-all
                       min-h-[140px] resize-y leading-relaxed"
            disabled={segmentationState.isProcessing}
          />

          <p className="text-xs text-[#6B7280] mt-4 bg-amber-50 border border-amber-200 rounded-lg p-3">
            <strong>Tip:</strong> The more specific you are about each group, the sharper the segments.
          </p>
        </div>

        {/* Progress Indicator - uses real backend progress only */}
        {(segmentationState.isProcessing || isFetchingResults) && (
          <div className="bg-[#E8FCFA] rounded-lg p-4">
            <div className="flex items-center gap-3 mb-2">
              <Loader2 className="w-4 h-4 animate-spin text-[#07E5D1]" />
              <span className="text-sm text-[#374151]">
                {isFetchingResults
                  ? 'Loading your segments...'
                  : (segmentationState.currentStep || 'Starting segmentation...')}
              </span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
              {/* Show indeterminate animation when no real progress, otherwise show actual progress */}
              {(segmentationState.progress || 0) > 0 || isFetchingResults ? (
                <div
                  className="bg-[#07E5D1] h-2 rounded-full transition-all duration-500 ease-out"
                  style={{ width: `${isFetchingResults ? 100 : segmentationState.progress}%` }}
                />
              ) : (
                <div className="h-2 bg-[#07E5D1] rounded-full animate-progress-indeterminate" />
              )}
            </div>
            {!isFetchingResults && (
              <div className="flex justify-between text-xs text-[#6B7280] mt-1">
                <span>
                  {(segmentationState.progress || 0) > 0
                    ? `${Math.round(segmentationState.progress)}%`
                    : 'Processing...'}
                </span>
                <button
                  onClick={cancelSegmentation}
                  className="text-red-500 hover:text-red-600 transition-colors"
                >
                  Cancel
                </button>
              </div>
            )}
          </div>
        )}

        {/* Error */}
        {segmentationState.error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-red-700">Segmentation Failed</p>
              <p className="text-sm text-red-600 mt-1">{segmentationState.error}</p>
            </div>
          </div>
        )}

      </div>

      {/* Results */}
      {selectedSegmentation && selectedSegmentation.status === 'completed' && (
        <DynamicSegmentResults
          segmentation={selectedSegmentation}
          onSelectSegment={handleSegmentSelection}
          selectedIndices={selectedSegmentIndices}
        />
      )}
    </div>
  );
});

export default DynamicSegmentationPanel;
